import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Loader from '../../components/Common/Loader';
import { Search } from 'lucide-react';

interface ObligationItem {
    symbole: string;
    nom_obligation: string | null;
    emetteur: string | null;
    taux_interet: number | null;
    date_echeance: string | null;
    // Cotation fields
    valeur_nominale: number | null;
    cours_precedent: number | null;
    cours_cloture: number | null;
    cours_reference: number | null;
    coupon_couru: number | null;
    periode: string | null;
    montant_net_coupon: number | null;
    type_amortissement: string | null;
    volume_titres: number | null;
    volume_fcfa: number | null;
    date_cotation: string | null;
}

// Filter options with clear labels
const PERIODE_OPTIONS = [
    { value: '', label: 'Toutes périodes' },
    { value: 'A', label: 'A - Annuelle' },
    { value: 'S', label: 'S - Semestrielle' },
    { value: 'T', label: 'T - Trimestrielle' },
];

const TYPE_AMORT_OPTIONS = [
    { value: '', label: "Tous types d'amortissement" },
    { value: 'IF', label: 'IF - In Fine' },
    { value: 'AC', label: 'AC - Amortissement Constant' },
    { value: 'AD', label: 'AD - Amortissement Dégressif' },
    { value: 'ACD', label: 'ACD - Amort. Constant Différé' },
];

const ObligationsScreen: React.FC = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [obligations, setObligations] = useState<ObligationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPeriode, setSelectedPeriode] = useState('');
    const [selectedTypeAmort, setSelectedTypeAmort] = useState('');

    useEffect(() => {
        if (token) fetchObligations();
    }, [token]);

    const fetchObligations = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);

            const response = await fetch(`${API_URL}/v1/obligations?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setObligations(data);
            }
        } catch (error) {
            console.error('Error fetching obligations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchObligations();
    };

    // Filter locally based on selected filters
    const filteredList = obligations.filter(item => {
        const matchesSearch = !searchTerm ||
            item.symbole?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.nom_obligation?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPeriode = !selectedPeriode || item.periode === selectedPeriode;
        const matchesTypeAmort = !selectedTypeAmort || item.type_amortissement === selectedTypeAmort;
        return matchesSearch && matchesPeriode && matchesTypeAmort;
    });

    const selectStyle = {
        padding: '10px 14px',
        borderRadius: '10px',
        border: '1px solid #e5e7eb',
        fontSize: '14px',
        background: 'var(--bg-box)',
        cursor: 'pointer',
        minWidth: '200px',
    };

    return (
        <SiteLayout>
            <Header title="Marché des Obligations" />
            <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}>
                {/* Filters Section */}
                <div style={{
                    background: 'var(--bg-box)',
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '24px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                }}>
                    {/* Search */}
                    <form onSubmit={handleSearch} style={{ flex: 1, minWidth: '250px' }}>
                        <div style={{ position: 'relative' }}>
                            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                            <input
                                type="text"
                                placeholder="Rechercher par symbole ou nom..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 12px 12px 44px',
                                    borderRadius: '12px',
                                    border: '1px solid #e5e7eb',
                                    fontSize: '14px',
                                }}
                            />
                        </div>
                    </form>

                    {/* Période Filter */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: 500 }}>
                            Périodicité
                        </label>
                        <select
                            value={selectedPeriode}
                            onChange={(e) => setSelectedPeriode(e.target.value)}
                            style={selectStyle}
                        >
                            {PERIODE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Type Amortissement Filter */}
                    <div>
                        <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: 500 }}>
                            Type d'Amortissement
                        </label>
                        <select
                            value={selectedTypeAmort}
                            onChange={(e) => setSelectedTypeAmort(e.target.value)}
                            style={selectStyle}
                        >
                            {TYPE_AMORT_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Results count */}
                    <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>
                        {filteredList.length} obligation{filteredList.length !== 1 ? 's' : ''}
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <Loader message="Chargement des obligations..." />
                ) : (
                    <div style={{ background: 'var(--bg-box)', borderRadius: '20px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                            <thead>
                                <tr style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                                    <th style={{ color: 'white', padding: '14px 10px', textAlign: 'left', fontSize: '13px' }}>Symbole</th>
                                    <th style={{ color: 'white', padding: '14px 10px', textAlign: 'left', fontSize: '13px' }}>Désignation</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Taux</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Cours Préc.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Cours Réf.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'right', fontSize: '13px' }}>Coupon</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Pér.</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Type</th>
                                    <th style={{ color: 'white', padding: '14px 8px', textAlign: 'center', fontSize: '13px' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((item) => (
                                    <tr
                                        key={item.symbole}
                                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                        onClick={() => navigate(`/obligations/${item.symbole}`)}
                                    >
                                        <td style={{ padding: '12px 10px', fontWeight: 700, color: '#10b981', fontSize: '13px' }}>{item.symbole}</td>
                                        <td style={{ padding: '12px 10px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>
                                            {item.nom_obligation || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px' }}>
                                            {item.taux_interet ? `${item.taux_interet.toFixed(2)}%` : '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px' }}>
                                            {item.cours_precedent?.toLocaleString('fr-FR', { minimumFractionDigits: 0 }) || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 600, fontSize: '13px' }}>
                                            {item.cours_reference?.toLocaleString('fr-FR', { minimumFractionDigits: 0 }) || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px' }}>
                                            {item.coupon_couru?.toLocaleString('fr-FR', { minimumFractionDigits: 2 }) || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px' }}>
                                            <span style={{ padding: '2px 8px', background: item.periode === 'A' ? '#dcfce7' : '#dbeafe', borderRadius: '4px', fontSize: '11px' }}>
                                                {item.periode || '-'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                                            {item.type_amortissement || '-'}
                                        </td>
                                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/obligations/${item.symbole}`); }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    background: '#d1fae5',
                                                    color: '#059669',
                                                    cursor: 'pointer',
                                                    fontWeight: 500,
                                                    fontSize: '12px',
                                                }}
                                            >
                                                Détails
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredList.length === 0 && (
                            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                Aucune obligation trouvée
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default ObligationsScreen;

